import type { MortalityData } from '@mortality/core';

const CACHE_VERSION = 7;
const cacheKey = `mortality:data:v${CACHE_VERSION}`;

interface CachedMortalityPayload {
  version: number;
  data: MortalityData;
  storedAt: number;
}

const readItem = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(cacheKey);
};

const writeItem = (value: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (value == null) {
    window.localStorage.removeItem(cacheKey);
  } else {
    window.localStorage.setItem(cacheKey, value);
  }
};

export async function readCachedMortalityData(): Promise<MortalityData | null> {
  const raw = readItem();
  if (!raw) {
    return null;
  }

  try {
    const payload = JSON.parse(raw) as CachedMortalityPayload;
    if (payload.version !== CACHE_VERSION || !payload.data) {
      writeItem(null);
      return null;
    }
    return payload.data;
  } catch (error) {
    console.warn('Failed to parse cached mortality data:', error);
    writeItem(null);
    return null;
  }
}

export async function writeCachedMortalityData(data: MortalityData): Promise<void> {
  const payload: CachedMortalityPayload = {
    version: CACHE_VERSION,
    data,
    storedAt: Date.now(),
  };
  writeItem(JSON.stringify(payload));
}

export async function clearCachedMortalityData(): Promise<void> {
  writeItem(null);
}
